/**
 * File Attachments Component
 *
 * Multi-file upload with drag & drop support:
 * - Max 5 files, 10MB each
 * - Supported: images (png, jpg, gif, webp), PDFs
 * - File previews with remove buttons
 */

import { useCallback, useEffect, useState } from 'react'
import { Upload, X, FileIcon, ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'

interface FileAttachmentsProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  maxFiles?: number
  maxSizeMB?: number
}

const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
}

const MAX_FILES = 5
const MAX_SIZE_MB = 10

/**
 * ImagePreview component with proper object URL lifecycle management
 * Uses useEffect to cleanup object URLs and prevent memory leaks
 */
function ImagePreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)

    // Cleanup: revoke URL on unmount or when file changes
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [file])

  if (!url) return null

  return (
    <img
      src={url}
      alt={file.name}
      className="h-12 w-12 object-cover rounded"
    />
  )
}

export function FileAttachments({
  files,
  onFilesChange,
  maxFiles = MAX_FILES,
  maxSizeMB = MAX_SIZE_MB,
}: FileAttachmentsProps) {
  const validateAndAddFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles)

      // Check total count
      if (files.length + fileArray.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`)
        return
      }

      // Validate each file
      const validFiles: File[] = []
      for (const file of fileArray) {
        // Check file type
        const isValidType = Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)
        if (!isValidType) {
          toast.error(`${file.name}: Unsupported file type. Use images or PDFs.`)
          continue
        }

        // Check file size (reject empty and oversized files)
        if (file.size === 0) {
          toast.error(`${file.name}: File is empty`)
          continue
        }
        const sizeMB = file.size / (1024 * 1024)
        if (sizeMB > maxSizeMB) {
          toast.error(`${file.name}: File too large (max ${maxSizeMB}MB)`)
          continue
        }

        validFiles.push(file)
      }

      if (validFiles.length > 0) {
        onFilesChange([...files, ...validFiles])
        toast.success(`${validFiles.length} file(s) added`)
      }
    },
    [files, maxFiles, maxSizeMB, onFilesChange]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        validateAndAddFiles(e.target.files)
      }
      // Reset input so same file can be selected again
      e.target.value = ''
    },
    [validateAndAddFiles]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        validateAndAddFiles(e.dataTransfer.files)
      }
    },
    [validateAndAddFiles]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index)
      onFilesChange(newFiles)
    },
    [files, onFilesChange]
  )

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon className="h-4 w-4 text-muted-foreground" />
    }
    return <FileIcon className="h-4 w-4 text-muted-foreground" />
  }

  const isImage = (file: File) => file.type.startsWith('image/')

  return (
    <div className="space-y-3">
      {/* Drag & Drop Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="relative border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 hover:border-muted-foreground/50 transition-colors"
      >
        <input
          id="file-upload"
          type="file"
          multiple
          accept={Object.keys(ACCEPTED_FILE_TYPES).join(',')}
          onChange={handleFileInput}
          className="sr-only"
        />
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <Upload className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-foreground mb-1">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground">
            Images (PNG, JPG, GIF, WebP) or PDFs (max {maxSizeMB}MB each)
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {files.length}/{maxFiles} files
          </p>
        </label>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-3 p-3 rounded-md bg-muted/50 border border-border"
            >
              {/* Preview or Icon */}
              <div className="flex-shrink-0">
                {isImage(file) ? (
                  <ImagePreview file={file} />
                ) : (
                  <div className="h-12 w-12 flex items-center justify-center bg-muted rounded">
                    {getFileIcon(file)}
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                </p>
              </div>

              {/* Remove Button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
